import { breakpoint } from "@porsche-design-system/components-js/styles";
import { routerStore } from "@phn/state/router-store";
import {
  constructRoutingKey,
  RoutingKeyPointers,
  RoutingKeys,
} from "@phn/entities/routing-key";
import { isMobile, isString } from "@phn/utility/helper";
import {
  getValidContentPathSegments,
  snakeToCamel,
} from "./string-helper";
import { navContentStore } from "@phn/state/nav-content-store";
import { Content, TMenu } from "@phn/entities/content";
import windowService from "@phn/services/window-service";
import animationManager from "./animation-manager";

const shouldGoToModels = (route: string): boolean =>
  // eslint-disable-next-line eqeqeq
  route === RoutingKeys.MAIN_MENU && navContentStore.state.models != null;

const shouldGoToVehiclePurchase = (route: string): boolean =>
  route === RoutingKeys.MAIN_MENU &&
  // eslint-disable-next-line eqeqeq
  navContentStore.state.models == null &&
  // eslint-disable-next-line eqeqeq
  navContentStore.state.vehiclePurchase != null;

export const getCurrentRoute = (isDoubleDrawer?: boolean): string => {
  const { routingKeyHistory, routingKeyPointer } = routerStore.state;
  const route = routingKeyHistory[routingKeyPointer] || "";
  return checkForDefaultRoute(route, isDoubleDrawer);
};

export const getAnimatedRoute = (useDefaultRoute?: boolean): string => {
  const { animatingHistory, animatingPointer } = routerStore.state;
  const route = animatingHistory[animatingPointer] || "";
  return checkForDefaultRoute(route, useDefaultRoute);
};

function checkForDefaultRoute(
  route: string,
  useDefaultRoute?: boolean,
): string {
  // Return default for double-drawer when resizing
  if (useDefaultRoute && shouldGoToModels(route)) {
    addToRoute(RoutingKeys.MODELS);
    return RoutingKeys.MODELS;
  }

  if (useDefaultRoute && shouldGoToVehiclePurchase(route)) {
    addToRoute(RoutingKeys.VEHICLE_PURCHASE);
    return RoutingKeys.VEHICLE_PURCHASE;
  }

  return route;
}

export const shouldShowBackButton = (section: string): boolean => {
  const sectionKey = routerStore.state.routingKeyHistory.indexOf(section);

  return sectionKey >= RoutingKeyPointers.LEVEL_TWO.valueOf();
};

export const goTo = (...routes: string[]): void => {
  if (!animationManager.isPlayingAny()) {
    const newHistory = [RoutingKeys.MAIN_MENU, ...routes];
    routerStore.state.routingKeyPointer = routes.length;
    routerStore.state.routingKeyHistory = newHistory;
  } else {
    setTimeout(() => goTo(...routes), 20);
  }
};

export const addToRoute = (...routes: string[]): void => {
  const newHistory = [RoutingKeys.MAIN_MENU, ...routes];
  routerStore.state.routingKeyPointer = routes.length;
  routerStore.state.routingKeyHistory = newHistory;
};

export const back = (): void => {
  if (!animationManager.isPlayingAny()) {
    routerStore.state.routingKeyPointer -= 1;
  }
};

export const pop = (): void => {
  if (!animationManager.isPlayingAny()) {
    if (isNavigatingBackwards()) routerStore.state.routingKeyHistory.pop();
  }
};

export const addRoute = (newRoute: string[]): void => {
  if (!animationManager.isPlayingAny()) {
    const newHistory = [
      ...routerStore.state.routingKeyHistory,
      constructRoutingKey(getCurrentRoute(), ...newRoute),
    ];
    routerStore.state.routingKeyPointer = newHistory.length - 1;
    routerStore.state.routingKeyHistory = newHistory;
  }
};

export const reset = (wasOpen = true): void => {
  if (!animationManager.isPlayingAny()) {
    if (wasOpen) windowService.unlockScroll();
    routerStore.state.routingKeyPointer = -1;
  }
};

export const isNavigatingBackwards = (): boolean =>
  routerStore.state.routingKeyPointer <
  routerStore.state.routingKeyHistory.length - 1;

export const isAnimating = (): boolean => {
  const { routingKeyPointer, animatingPointer } = routerStore.state;
  return routingKeyPointer !== animatingPointer;
};

export const isAnimatingForward = (): boolean => {
  const { routingKeyPointer, animatingPointer } = routerStore.state;

  return routingKeyPointer > animatingPointer;
};

export const isAnimatingBackward = (): boolean => {
  const { routingKeyPointer, animatingPointer } = routerStore.state;
  return routingKeyPointer < animatingPointer;
};

export const isAnimatingSameLevel = (): boolean => {
  const {
    animatingHistory,
    routingKeyHistory,
    animatingPointer,
    routingKeyPointer,
  } = routerStore.state;

  return (
    animatingHistory !== routingKeyHistory &&
    animatingPointer === routingKeyPointer
  );
};

export const isOpening = (): boolean =>
  routerStore.state.animatingPointer ===
    RoutingKeyPointers.LEVEL_ZERO.valueOf() &&
  routerStore.state.routingKeyPointer >= 0;

export const isOpened = (): boolean => !isClosed();

export const isClosed = (): boolean =>
  routerStore.state.routingKeyPointer ===
    RoutingKeyPointers.LEVEL_ZERO.valueOf() &&
  routerStore.state.animatingPointer ===
    RoutingKeyPointers.LEVEL_ZERO.valueOf();
export const isClosing = (): boolean =>
  routerStore.state.routingKeyPointer === -1 &&
  routerStore.state.animatingPointer >= 0;

export const isResized = (): boolean => {
  const breakpointChanged =
    routerStore.state.animatingBreakpoint !== routerStore.state.breakpoint;
  const changeToDesktop =
    routerStore.state.animatingBreakpoint <= breakpoint.xs &&
    routerStore.state.breakpoint >= breakpoint.s;
  const changeToMobile =
    routerStore.state.animatingBreakpoint >= breakpoint.s &&
    routerStore.state.breakpoint <= breakpoint.xs;

  return breakpointChanged && (changeToDesktop || changeToMobile);
};

export const syncAnimation = (): void => {
  routerStore.state.animatingPointer = routerStore.state.routingKeyPointer;
  routerStore.state.animatingHistory = routerStore.state.routingKeyHistory;
  routerStore.state.animatingBreakpoint = routerStore.state.breakpoint;
};

export function mountRouteFromString(route?: string): string[] {
  const initialRoute = navContentStore.state.models
    ? RoutingKeys.MODELS
    : RoutingKeys.VEHICLE_PURCHASE;
  const defaultRoute = isMobile(routerStore.state.breakpoint)
    ? []
    : [initialRoute];

  try {
    if (!isString(route)) return defaultRoute;
    const [topLevelRoutingKey, ...segments] = route.split(">");
    const rootContentItem: TMenu | null = navContentStore.state[
      snakeToCamel(topLevelRoutingKey) as keyof Content
    ] as TMenu;
    if (!rootContentItem) return defaultRoute;

    const validSegments = getValidContentPathSegments(
      rootContentItem,
      segments,
    );
    return buildRoutingKeyHistoryFromContentPath([
      topLevelRoutingKey,
      ...validSegments,
    ]);
  } catch (e) {
    return defaultRoute;
  }
}

export function buildRoutingKeyHistoryFromContentPath(
  segments: string[],
): string[] {
  const routingKeyHistory = [segments[0]];
  for (let i = 1; i < segments.length; i += 1) {
    routingKeyHistory.push(`${routingKeyHistory[i - 1]}/${segments[i]}`);
  }
  return routingKeyHistory;
}
